/**
 * I vocali del pannello di MUNE (fetta 2G, 29/10/2026; os-planning/ai/fase-2g-vocali.md). Lorenzo:
 * «allegare, mandare audio o inviare vocale trascritto»; il 29/10 «usa groq», e per la costruzione
 * «più lento», cioè fatta da una sessione sola: qui la versione asciutta del progetto.
 *
 * - Registrare: MediaRecorder a 32 kbit/s, mono; si ferma da solo a 5 minuti. Il microfono si
 *   spegne appena la registrazione finisce o si annulla.
 * - Un vocale già registrato (WhatsApp, Memo vocali, MP3) entra dalla graffetta, dal trascinamento
 *   o da Ctrl+V: il pannello lo smista qui invece che nella striscia degli allegati.
 * - Ogni vocale va da solo a POST /ai/vocali, uno dopo l'altro (al massimo 5 in coda), e il testo
 *   torna al pannello, che lo mette nel campo: lo manda chi chiede.
 * - L'audio vive solo in memoria (un Blob), finché non è trascritto: niente IndexedDB,
 *   localStorage o indirizzi blob:.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LIMITI_VOCALI, type VocaleTrascritto } from '@masamune/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { fraseErroreVocale, PAROLE_VOCALI, TIPI_REGISTRAZIONE } from './vocaliTesto';

/** fermo: niente in corso; chiedo: il browser chiede il permesso del microfono; registro: si registra. */
export type FaseVocale = 'fermo' | 'chiedo' | 'registro';

interface Voce {
  audio: Blob;
  /** Il nome che va al server: sempre «vocale.<estensione>», mai quello del file. */
  nomeFile: string;
  /** Il nome del file allegato, per le frasi; null per un vocale registrato dal pannello. */
  nome: string | null;
}

export interface UsoVocali {
  fase: FaseVocale;
  /** I secondi registrati finora. */
  secondi: number;
  /** I vocali che aspettano il testo, quello in corso compreso. */
  inCoda: number;
  registra: () => void;
  /** Ferma e trascrive. */
  ferma: () => void;
  /** Ferma e butta. */
  annulla: () => void;
  trascriviFile: (file: File[]) => void;
}

/** Il browser sa registrare: MediaRecorder e il microfono (in http, fuori da localhost, no). */
export function registrazionePossibile(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

function trascrivi(v: Voce): Promise<VocaleTrascritto> {
  const corpo = new FormData();
  corpo.append('file', v.audio, v.nomeFile);
  return apiFetch<VocaleTrascritto>('/ai/vocali', { method: 'POST', body: corpo });
}

export function useVocali(p: { onTesto: (testo: string) => void; onErrore: (frase: string) => void }): UsoVocali {
  const [fase, setFase] = useState<FaseVocale>('fermo');
  const [secondi, setSecondi] = useState(0);
  const [inCoda, setInCoda] = useState(0);
  // Le funzioni del pannello dell'ultimo disegno: il registratore le chiama più tardi.
  const cb = useRef(p);
  cb.current = p;
  const registratore = useRef<MediaRecorder | null>(null);
  const microfono = useRef<MediaStream | null>(null);
  const pezzi = useRef<Blob[]>([]);
  const butta = useRef(false);
  const partito = useRef(0);
  const orologio = useRef<number>();
  const coda = useRef<Voce[]>([]);
  const lavora = useRef(false);
  const montato = useRef(true);
  // Il numero della richiesta del microfono: Annulla mentre il browser chiede il permesso la rende
  // vecchia, e quando il permesso arriva non parte niente.
  const richiesta = useRef(0);

  const spegniMicrofono = () => {
    microfono.current?.getTracks().forEach((t) => t.stop());
    microfono.current = null;
    window.clearInterval(orologio.current);
  };

  /** I vocali in coda, uno alla volta: il testo al pannello, l'errore con la sua frase. */
  const prossimo = async () => {
    if (lavora.current) return;
    lavora.current = true;
    try {
      while (coda.current.length) {
        const v = coda.current[0]!;
        try {
          const r = await trascrivi(v);
          if (montato.current) cb.current.onTesto(r.testo);
        } catch (e) {
          if (montato.current) {
            cb.current.onErrore(
              e instanceof ApiError
                ? fraseErroreVocale(e.code, e.details, v.nome, e.message)
                : 'Il vocale non è partito: controlla la connessione e riprova.',
            );
          }
        } finally {
          coda.current.shift();
          if (montato.current) setInCoda(coda.current.length);
        }
      }
    } finally {
      lavora.current = false;
    }
  };

  const accoda = (v: Voce): boolean => {
    if (coda.current.length >= LIMITI_VOCALI.perVolta) {
      cb.current.onErrore(PAROLE_VOCALI.troppiInCoda);
      return false;
    }
    coda.current.push(v);
    setInCoda(coda.current.length);
    void prossimo();
    return true;
  };

  const ferma = useCallback(() => {
    const r = registratore.current;
    if (r && r.state !== 'inactive') {
      butta.current = false;
      r.stop();
    }
  }, []);

  const annulla = useCallback(() => {
    richiesta.current++;
    const r = registratore.current;
    if (r && r.state !== 'inactive') {
      butta.current = true;
      r.stop();
      return;
    }
    spegniMicrofono();
    setFase('fermo');
  }, []);

  const registra = useCallback(() => {
    if (registratore.current || microfono.current) return;
    if (!registrazionePossibile()) {
      cb.current.onErrore(PAROLE_VOCALI.registrazioneNonVa);
      return;
    }
    setFase('chiedo');
    const questa = ++richiesta.current;
    void (async () => {
      let flusso: MediaStream;
      try {
        flusso = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      } catch (e) {
        if (!montato.current || questa !== richiesta.current) return;
        setFase('fermo');
        const nome = (e as { name?: string } | null)?.name;
        cb.current.onErrore(
          nome === 'NotAllowedError' || nome === 'SecurityError'
            ? PAROLE_VOCALI.microfonoNegato
            : nome === 'NotFoundError' || nome === 'OverconstrainedError'
              ? PAROLE_VOCALI.microfonoAssente
              : PAROLE_VOCALI.microfonoOccupato,
        );
        return;
      }
      if (!montato.current || questa !== richiesta.current) {
        flusso.getTracks().forEach((t) => t.stop());
        return;
      }
      const scelto = TIPI_REGISTRAZIONE.find((t) => MediaRecorder.isTypeSupported(t.tipo));
      let r: MediaRecorder;
      try {
        r = new MediaRecorder(flusso, { ...(scelto ? { mimeType: scelto.tipo } : {}), audioBitsPerSecond: LIMITI_VOCALI.kbps * 1000 });
      } catch {
        flusso.getTracks().forEach((t) => t.stop());
        setFase('fermo');
        cb.current.onErrore(PAROLE_VOCALI.registrazioneNonVa);
        return;
      }
      microfono.current = flusso;
      registratore.current = r;
      pezzi.current = [];
      butta.current = false;
      r.ondataavailable = (ev) => {
        if (ev.data.size) pezzi.current.push(ev.data);
      };
      r.onstop = () => {
        spegniMicrofono();
        registratore.current = null;
        const tipo = (r.mimeType || scelto?.tipo || 'audio/webm').split(';')[0]!;
        const ext = tipo.includes('mp4') ? 'm4a' : tipo.includes('ogg') ? 'ogg' : 'webm';
        const audio = new Blob(pezzi.current, { type: tipo });
        pezzi.current = [];
        if (montato.current) {
          setFase('fermo');
          setSecondi(0);
        }
        if (butta.current || !audio.size || !montato.current) return;
        accoda({ audio, nomeFile: `vocale.${ext}`, nome: null });
      };
      partito.current = Date.now();
      setSecondi(0);
      r.start(1000);
      setFase('registro');
      orologio.current = window.setInterval(() => {
        const s = (Date.now() - partito.current) / 1000;
        setSecondi(s);
        if (s >= LIMITI_VOCALI.secondi) ferma();
      }, 250);
    })();
    // accoda e spegniMicrofono leggono solo ref: stabili.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ferma]);

  const trascriviFile = useCallback((file: File[]) => {
    for (const f of file) {
      if (f.size > LIMITI_VOCALI.mb * 1024 * 1024) {
        cb.current.onErrore(fraseErroreVocale('VOCALE_PESO', null, f.name, ''));
        continue;
      }
      const ext = (f.name.includes('.') ? f.name.split('.').pop()! : 'ogg').toLowerCase().slice(0, 5);
      if (!accoda({ audio: f, nomeFile: `vocale.${ext}`, nome: f.name })) break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Smontato il pannello: la registrazione si butta e il microfono si spegne.
  useEffect(() => {
    montato.current = true;
    return () => {
      montato.current = false;
      richiesta.current++;
      const r = registratore.current;
      if (r && r.state !== 'inactive') {
        butta.current = true;
        r.stop();
      }
      spegniMicrofono();
    };
  }, []);

  return { fase, secondi, inCoda, registra, ferma, annulla, trascriviFile };
}
